import {
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  Boxes,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  Database,
  Focus,
  Layers3,
  LocateFixed,
  Map as MapIcon,
  MapPin,
  Maximize2,
  MoveRight,
  PackageCheck,
  PackageOpen,
  Route,
  ScanLine,
  Search,
  ShieldAlert,
  TrendingUp,
  Warehouse,
} from "lucide-react";
import { Fragment, startTransition, useEffect, useMemo, useState } from "react";
import type { PageId } from "../components/Shell";
import {
  WarehouseMap2D,
  type LocationFilter,
  type WarehouseSelection,
} from "../components/WarehouseMap2D";
import {
  buildLocationInventory,
  buildWarehouseLayout,
  findLocation,
  formatInventoryUpdate,
  normalizeLocationCode,
  normalizeZoneCode,
  racksFor,
  stockStateLabel,
  warehouseZone,
  WAREHOUSE_LOCATION_COUNT,
  WAREHOUSE_SPECIAL_LOCATIONS,
  WAREHOUSE_SPECIAL_ZONE,
  WAREHOUSE_ZONE_COUNT,
  WAREHOUSE_ZONES,
  type WarehouseZoneSpec,
} from "../lib/warehouse";
import {
  getWarehouseStock,
  getWarehouseZones,
  warehouseApiConfigured,
  type WarehouseInventoryLocationPatch,
  type WarehouseZoneSnapshotData,
} from "../services/warehouseApi";
import { shanghaiDateString } from "../lib/batch";
import {
  buildExpiryInsights,
  expiryCategoryLabel,
  productMatchesExpiryView,
  type ExpiryCategory,
  type ExpiryProductInsight,
  type ExpiryViewFilter,
} from "../lib/expiry-insights";
import type {
  DecisionPlan,
  LocationNode,
  ProductAnalysis,
  WarehouseDataset,
} from "../types";

interface WarehousePageProps {
  dataset: WarehouseDataset;
  plan: DecisionPlan;
  onNavigate: (page: PageId) => void;
}

const initialSelection: WarehouseSelection = {
  level: "zones",
  mainAisle: null,
  smallAisle: null,
  selectedCode: null,
};

const EMPTY_INVENTORY_OVERRIDES: ReadonlyMap<string, WarehouseInventoryLocationPatch> =
  new Map();

type WarehouseDataStatus = "STATIC READY" | "SYNCING" | "BACKEND LIVE" | "STATIC FALLBACK";

function parentLevel(selection: WarehouseSelection): WarehouseSelection {
  if (selection.level === "rack") {
    if (!selection.mainAisle) {
      return {
        ...initialSelection,
        level: "overview",
        selectedCode: selection.selectedCode,
      };
    }
    return {
      level: "small",
      mainAisle: selection.mainAisle,
      smallAisle: selection.smallAisle,
      selectedCode: selection.selectedCode,
    };
  }
  if (selection.level === "small" && selection.mainAisle) {
    return {
      level: "main",
      mainAisle: selection.mainAisle,
      smallAisle: null,
      selectedCode: selection.selectedCode,
    };
  }
  if (selection.level === "main") {
    return {
      level: "overview",
      mainAisle: null,
      smallAisle: null,
      selectedCode: selection.selectedCode,
    };
  }
  if (selection.level === "zones") return selection;
  return initialSelection;
}

function formatExpiry(value: string) {
  if (!value) return "未提供";
  const date = new Date(`${value}T12:00:00+08:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function remainingRatioPercent(ratio: number | null | undefined) {
  if (ratio == null || !Number.isFinite(ratio)) return null;
  const clamped = Math.max(0, Math.min(1, ratio));
  return Math.round(clamped * 100);
}

function expiryReachedLabel(category: ExpiryCategory) {
  if (category === "quarter") return "已到 ¼ 临期";
  if (category === "third") return "已到 ⅓ 临期";
  if (category === "half") return "已到 ½ 临期";
  if (category === "fresh") return "新鲜期";
  if (category === "expired") return "已过期";
  return "待核对";
}

function levelLabel(level: WarehouseSelection["level"], special = false) {
  if (level === "zones") return `${WAREHOUSE_ZONE_COUNT} 个真实库区总览`;
  if (level === "overview") return special ? "特殊库位总览" : "库区总览";
  if (level === "main") return "主通道视图";
  if (level === "small") return "小通道视图";
  if (level === "rack") return "单货架视图";
  return "完整库位图";
}

const warehouseLevelSteps = [
  {
    level: "zones",
    step: "01",
    title: "选择库区",
    detail: `${WAREHOUSE_ZONE_COUNT} 个真实库区，A 区紧邻出库口`,
  },
  {
    level: "overview",
    step: "02",
    title: "选择主通道",
    detail: "进入库区，选择要查看的主通道",
  },
  {
    level: "main",
    step: "03",
    title: "选择小通道",
    detail: "沿主通道定位目标小通道",
  },
  {
    level: "small",
    step: "04",
    title: "查看货架",
    detail: "有货为浅绿色，并显示当前货物",
  },
  {
    level: "rack",
    step: "05",
    title: "查看库存",
    detail: "核对货物、SKU、批次、效期与距离",
  },
] as const;

type WarehouseStage = (typeof warehouseLevelSteps)[number]["level"];

function warehouseStageIndex(level: WarehouseSelection["level"]) {
  if (level === "full") return 1;
  return warehouseLevelSteps.findIndex((step) => step.level === level);
}

const expiryCategoryOrder: ExpiryCategory[] = [
  "quarter",
  "third",
  "half",
  "fresh",
  "expired",
  "unknown",
];

function productMatchesQuery(product: ExpiryProductInsight, rawQuery: string) {
  const query = rawQuery.trim().toLocaleLowerCase("zh-CN");
  if (!query) return true;
  return [
    product.product,
    product.sku,
    product.category,
    expiryCategoryLabel(product.mostUrgentClass),
    ...product.locations,
    ...product.batches.flatMap((batch) => [
      batch.batch.lot,
      batch.batch.product,
      batch.recommendedRack ?? "",
      normalizeLocationCode(batch.batch.location),
    ]),
  ].some((value) => value.toLocaleLowerCase("zh-CN").includes(query));
}

function formatMetric(value: number) {
  return value.toLocaleString("zh-CN");
}

export function WarehousePage({ dataset, plan, onNavigate }: WarehousePageProps) {
  const [activeZone, setActiveZone] = useState("A");
  const [liveZones, setLiveZones] = useState<WarehouseZoneSnapshotData[] | null>(null);
  const [inventoryOverrides, setInventoryOverrides] = useState(EMPTY_INVENTORY_OVERRIDES);
  const [dataStatus, setDataStatus] = useState<WarehouseDataStatus>(
    warehouseApiConfigured ? "SYNCING" : "STATIC READY",
  );
  const zoneSpecs = useMemo<readonly WarehouseZoneSpec[]>(() => {
    if (!liveZones?.length) return WAREHOUSE_ZONES;
    const liveByCode = new Map(liveZones.map((zone) => [zone.code, zone]));
    return WAREHOUSE_ZONES.map((zone) => {
      const live = liveByCode.get(zone.code);
      return live
        ? {
          ...zone,
          observedCount: live.observedCount,
          nearestOutbound: live.nearestOutbound ?? zone.nearestOutbound,
        }
        : zone;
    });
  }, [liveZones]);
  const layout = useMemo(
    () => buildWarehouseLayout(dataset, activeZone, inventoryOverrides),
    [activeZone, dataset, inventoryOverrides],
  );
  const inventoryByLocation = useMemo(() => buildLocationInventory(dataset), [dataset]);
  const [selection, setSelection] = useState<WarehouseSelection>(initialSelection);
  const [filter, setFilter] = useState<LocationFilter>("all");
  const [today, setToday] = useState(shanghaiDateString);
  const [expiryView, setExpiryView] = useState<ExpiryViewFilter>("all");
  const [expiryQuery, setExpiryQuery] = useState("");
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const expiryInsights = useMemo(
    () => buildExpiryInsights(dataset, plan, layout, today),
    [dataset, plan, layout, today],
  );
  const visibleExpiryProducts = useMemo(
    () => expiryInsights.products.filter(
      (product) =>
        productMatchesExpiryView(product, expiryView)
        && productMatchesQuery(product, expiryQuery),
    ),
    [expiryInsights.products, expiryQuery, expiryView],
  );
  const expiryTotals = useMemo(() => {
    const nearExpiry = expiryInsights.batches.filter(
      (batch) => ["quarter", "third", "half"].includes(batch.snapshot.expiryClass),
    );
    return {
      nearQuantity: nearExpiry.reduce((sum, batch) => sum + Math.max(0, batch.batch.quantity), 0),
      nearBatches: nearExpiry.length,
      occupiedLocations: new Set(
        nearExpiry.map((batch) => normalizeLocationCode(batch.batch.location)).filter(Boolean),
      ).size,
    };
  }, [expiryInsights.batches]);
  const riskProductCount = useMemo(
    () => expiryInsights.products.filter(
      (product) => productMatchesExpiryView(product, "risk"),
    ).length,
    [expiryInsights.products],
  );

  useEffect(() => {
    const timer = window.setInterval(() => setToday(shanghaiDateString()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!warehouseApiConfigured) return;
    const controller = new AbortController();
    getWarehouseZones(controller.signal)
      .then((snapshot) => {
        startTransition(() => {
          setLiveZones(snapshot.data);
          setDataStatus("BACKEND LIVE");
        });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        startTransition(() => setDataStatus("STATIC FALLBACK"));
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!warehouseApiConfigured) return;
    const controller = new AbortController();
    setDataStatus("SYNCING");
    getWarehouseStock(activeZone, controller.signal)
      .then((snapshot) => {
        const patches = new Map<string, WarehouseInventoryLocationPatch>();
        snapshot.data.locations.forEach((patch) => {
          const code = normalizeLocationCode(patch.code);
          if (code) patches.set(code, patch);
        });
        startTransition(() => {
          setInventoryOverrides(patches);
          setDataStatus("BACKEND LIVE");
        });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        startTransition(() => {
          setInventoryOverrides(EMPTY_INVENTORY_OVERRIDES);
          setDataStatus("STATIC FALLBACK");
        });
      });
    return () => controller.abort();
  }, [activeZone]);

  const productsByLocation = useMemo(() => {
    const grouped = new Map<string, ProductAnalysis[]>();
    plan.products.forEach((product) => {
      const code = normalizeLocationCode(product.location);
      if (!code) return;
      const products = grouped.get(code) ?? [];
      products.push(product);
      grouped.set(code, products);
    });
    grouped.forEach((products) => {
      products.sort((a, b) => b.forecastDaily - a.forecastDaily);
    });
    return grouped;
  }, [plan.products]);

  const batchesByLocation = useMemo(() => {
    const grouped = new Map<string, typeof dataset.batches>();
    dataset.batches.forEach((batch) => {
      const code = normalizeLocationCode(batch.location);
      if (!code) return;
      const rows = grouped.get(code) ?? [];
      rows.push(batch);
      grouped.set(code, rows);
    });
    grouped.forEach((rows) => {
      rows.sort((a, b) => a.expiresOn.localeCompare(b.expiresOn));
    });
    return grouped;
  }, [dataset.batches]);

  const productNamesByLocation = useMemo(() => {
    const grouped = new Map<string, string[]>();
    const add = (location: string, name: string | undefined) => {
      const code = normalizeLocationCode(location);
      const normalizedName = name?.trim();
      if (!code || !normalizedName) return;
      const names = grouped.get(code) ?? [];
      if (!names.includes(normalizedName)) names.push(normalizedName);
      grouped.set(code, names);
    };

    plan.products.forEach((product) => add(product.location, product.product));
    dataset.batches.forEach((batch) => add(batch.location, batch.product));
    inventoryOverrides.forEach((patch, code) => {
      patch.productNames?.forEach((name) => add(code, name));
    });
    return grouped;
  }, [dataset.batches, inventoryOverrides, plan.products]);

  const selected = selection.level === "zones"
    ? null
    : findLocation(layout, selection.selectedCode ?? "")
      ?? layout.nodes.find((node) => node.stockState === "stocked")
      ?? layout.nodes[0];
  const selectedProducts = selected ? productsByLocation.get(selected.code) ?? [] : [];
  const selectedProductNames = selected
    ? productNamesByLocation.get(selected.code) ?? []
    : [];
  const selectedInventory = selected ? inventoryByLocation.get(selected.code) : undefined;
  const selectedPlacement = useMemo(
    () => plan.placements.find((move) => move.sku === selectedProducts[0]?.sku),
    [plan.placements, selectedProducts],
  );
  const selectedBatches = useMemo(() => {
    if (!selected) return [];
    return batchesByLocation.get(selected.code) ?? [];
  }, [batchesByLocation, selected]);

  function openMain(mainAisle: string) {
    if (!mainAisle) {
      setSelection((current) => ({
        ...initialSelection,
        selectedCode: current.selectedCode,
      }));
      return;
    }
    const firstRack = layout.nodes.find(
      (node) => node.mainAisle === mainAisle && node.stockState === "stocked",
    ) ?? layout.nodes.find((node) => node.mainAisle === mainAisle);
    setSelection({
      level: "main",
      mainAisle,
      smallAisle: null,
      selectedCode: firstRack?.code ?? selection.selectedCode,
    });
  }

  function openZone(zone: string) {
    const normalizedZone = normalizeZoneCode(zone);
    if (!normalizedZone) return;
    setActiveZone(normalizedZone);
    setFilter("all");
    setSelection({
      level: "overview",
      mainAisle: null,
      smallAisle: null,
      selectedCode: null,
    });
  }

  function openSmall(mainAisle: string, smallAisle: string) {
    const racks = racksFor(layout, mainAisle, smallAisle);
    const firstRack = racks.find((node) => node.stockState === "stocked") ?? racks[0];
    setSelection({
      level: "small",
      mainAisle,
      smallAisle,
      selectedCode: firstRack?.code ?? null,
    });
  }

  function openFullMap() {
    setSelection((current) => ({
      level: "full",
      mainAisle: null,
      smallAisle: null,
      selectedCode: current.selectedCode,
    }));
  }

  function navigateToRack(code: string) {
    const normalizedCode = normalizeLocationCode(code);
    const targetZone = normalizeZoneCode(normalizedCode);
    if (!targetZone) return;
    const targetLayout = targetZone === activeZone
      ? layout
      : buildWarehouseLayout(dataset, targetZone, inventoryOverrides);
    const node = findLocation(targetLayout, normalizedCode);
    if (!node) return;
    setActiveZone(targetZone);
    setFilter("all");
    setSelection({
      level: "rack",
      mainAisle: node.mainAisle,
      smallAisle: node.smallAisle,
      selectedCode: node.code,
    });
  }

  function selectRack(node: LocationNode) {
    setSelection({
      level: "rack",
      mainAisle: node.mainAisle,
      smallAisle: node.smallAisle,
      selectedCode: node.code,
    });
  }

  function reselectZones() {
    setFilter("all");
    setSelection(initialSelection);
  }

  function openZoneOverview() {
    setFilter("all");
    setSelection((current) => ({
      ...initialSelection,
      level: "overview",
      selectedCode: current.selectedCode,
    }));
  }

  function goBack() {
    setFilter("all");
    setSelection(parentLevel(selection));
  }

  function navigateStage(level: WarehouseStage) {
    if (level === "zones") {
      reselectZones();
      return;
    }
    if (level === "overview") {
      openZoneOverview();
      return;
    }
    if (level === "main" && selection.mainAisle) {
      openMain(selection.mainAisle);
      return;
    }
    if (level === "small" && selection.mainAisle && selection.smallAisle) {
      openSmall(selection.mainAisle, selection.smallAisle);
      return;
    }
    if (level === "rack" && selection.selectedCode) {
      const node = layout.nodeByCode.get(selection.selectedCode);
      if (node) selectRack(node);
    }
  }

  const activeZoneSpec = warehouseZone(activeZone);
  const isSpecialZone = activeZone === WAREHOUSE_SPECIAL_ZONE;
  const activeZoneKind = isSpecialZone
    ? "特殊库位"
    : activeZoneSpec?.kind === "auxiliary" ? "辅库区" : "主库区";
  const zoneCrumbLabel = isSpecialZone ? "特殊库位" : `${activeZone} ${activeZoneKind}`;
  const currentStageIndex = warehouseStageIndex(selection.level);
  const backTargetLabel =
    selection.level === "rack"
      ? selection.mainAisle
        ? `${activeZone}${selection.mainAisle}-${selection.smallAisle ?? ""} 小通道`
        : `${activeZone} 库区总览`
      : selection.level === "small"
        ? `${activeZone}${selection.mainAisle ?? ""} 主通道`
        : selection.level === "main" || selection.level === "full"
          ? `${activeZone} 库区总览`
          : "全部库区";

  const breadcrumb = (
    <nav className="warehouse-breadcrumb" aria-label="仓库层级导航">
      <button
        type="button"
        className={selection.level === "zones" ? "is-current" : ""}
        aria-current={selection.level === "zones" ? "page" : undefined}
        onClick={reselectZones}
      >
        <Warehouse size={13} /> {WAREHOUSE_ZONE_COUNT} 库区
      </button>
      {selection.level !== "zones" && (
        <>
          <ChevronRight size={12} />
          {selection.level === "overview" ? (
            <span className="is-current" aria-current="page">
              <MapPin size={12} /> {zoneCrumbLabel}
            </span>
          ) : (
            <button type="button" onClick={openZoneOverview}>
              <MapPin size={12} /> {zoneCrumbLabel}
            </button>
          )}
        </>
      )}
      {selection.level === "full" && (
        <>
          <ChevronRight size={12} />
          <span className="is-current" aria-current="page">
            <ScanLine size={12} /> 完整库位图
          </span>
        </>
      )}
      {!isSpecialZone && selection.mainAisle && (
        <>
          <ChevronRight size={12} />
          {selection.level === "main" ? (
            <span className="is-current" aria-current="page">
              {selection.mainAisle} 主通道
            </span>
          ) : (
            <button type="button" onClick={() => openMain(selection.mainAisle!)}>
              {selection.mainAisle} 主通道
            </button>
          )}
        </>
      )}
      {!isSpecialZone && selection.smallAisle && (
        <>
          <ChevronRight size={12} />
          {selection.level === "small" ? (
            <span className="is-current" aria-current="page">
              {selection.smallAisle} 小通道
            </span>
          ) : (
            <button
              type="button"
              onClick={() => openSmall(selection.mainAisle!, selection.smallAisle!)}
            >
              {selection.smallAisle} 小通道
            </button>
          )}
        </>
      )}
      {selection.level === "rack" && selection.selectedCode && (
        <>
          <ChevronRight size={12} />
          <span className="is-current" aria-current="page">
            <ScanLine size={12} /> 货架 {selection.selectedCode}
          </span>
        </>
      )}
    </nav>
  );

  return (
    <div className="warehouse-page warehouse-2d-page">
      <section className="map-heading">
        <div>
          <span className="section-kicker">
            {WAREHOUSE_ZONE_COUNT} ZONES + {WAREHOUSE_SPECIAL_LOCATIONS.length} SPECIAL · LIVE WAREHOUSE DIGITAL TWIN
          </span>
          <h2>先看全仓库区，<br />再进入每一个货架。</h2>
        </div>
        <div className="map-heading-actions">
          <span className="data-rule">
            {WAREHOUSE_ZONE_COUNT} 个真实库区 · {WAREHOUSE_LOCATION_COUNT.toLocaleString("zh-CN")} 个库位 · 逐层下钻至单货架
          </span>
          <div className="warehouse-demo-note">
            <MapIcon size={16} />
            <span>二维实时底图<br /><b>点击逐级下钻</b></span>
          </div>
        </div>
      </section>

      <section className="map-stage">
        <div className="map-toolbar">
          {breadcrumb}
          {selection.level !== "zones" && (
            <div className="location-source-switch warehouse-filter" role="group" aria-label="货架库存状态筛选">
              <button className={filter === "all" ? "is-active" : ""} onClick={() => setFilter("all")}>
                全部 <b>{layout.stats.racks}</b>
              </button>
              <button className={filter === "stocked" ? "is-active" : ""} onClick={() => setFilter("stocked")}>
                有货 <b>{layout.stats.stocked}</b>
              </button>
              <button className={filter === "empty" ? "is-active" : ""} onClick={() => setFilter("empty")}>
                空架 <b>{layout.stats.empty}</b>
              </button>
              <button className={filter === "unknown" ? "is-active" : ""} onClick={() => setFilter("unknown")}>
                待核对 <b>{layout.stats.unknown}</b>
              </button>
            </div>
          )}
          <div className="warehouse-map-actions">
            {selection.level !== "zones" && (
              <button
                type="button"
                className="spatial-back is-labeled"
                aria-label={`返回上一级，${backTargetLabel}`}
                onClick={goBack}
              >
                <ChevronLeft size={16} />
                <span className="warehouse-back-copy">
                  <strong>返回上一级</strong>
                  <small>{backTargetLabel}</small>
                </span>
                <kbd>ESC</kbd>
              </button>
            )}
            {selection.level !== "zones" && (
              <button
                type="button"
                className="warehouse-reselect-zone"
                onClick={reselectZones}
              >
                <Layers3 size={14} /><span>重选库区</span>
              </button>
            )}
            {!isSpecialZone && (
              <button
                type="button"
                className={`warehouse-full-button ${selection.level === "full" ? "is-active" : ""}`}
                onClick={openFullMap}
                disabled={selection.level === "zones"}
              >
                <Maximize2 size={14} /><span>查看全图</span>
              </button>
            )}
            <button type="button" className="spatial-locate" onClick={() => selected && navigateToRack(selected.code)}>
              <Focus size={15} /><span>定位货架</span>
            </button>
          </div>
        </div>

        <div className="spatial-stage-shell">
          <WarehouseMap2D
            layout={layout}
            zones={zoneSpecs}
            selection={selection}
            filter={filter}
            products={productsByLocation}
            productNamesByLocation={productNamesByLocation}
            batchesByLocation={batchesByLocation}
            dataStatus={dataStatus}
            recommendedLocations={expiryInsights.recommendedLocations}
            onOpenZone={openZone}
            onOpenMain={openMain}
            onOpenSmall={openSmall}
            onOpenRack={selectRack}
            onBack={goBack}
          />
          <div className="map-view-status">
            <i />
            <span>{levelLabel(selection.level, isSpecialZone)}</span>
            <b className={`warehouse-sync-state is-${dataStatus.toLowerCase().replaceAll(" ", "-")}`}>
              {dataStatus}
            </b>
          </div>
          <div className="map-legend spatial-legend">
            {selection.level === "zones" ? (
              <>
                <span><i className="legend-main-zone" />主库区</span>
                <span><i className="legend-aux-zone" />辅库区</span>
                <span><i className="legend-nearest-zone" />最近出库口</span>
              </>
            ) : isSpecialZone ? (
              <>
                <span><i className="legend-main-aisle" />特殊库位</span>
                <span><i className="legend-stocked" />已关联库存</span>
                <span><i className="legend-unknown" />待核对</span>
              </>
            ) : (
              <>
                <span><i className="legend-main-aisle" />主通道</span>
                <span><i className="legend-stocked" />有货</span>
                <span><i className="legend-empty" />空架</span>
                <span><i className="legend-unknown" />待核对</span>
              </>
            )}
          </div>
          <div className="spatial-corner spatial-corner-tl" />
          <div className="spatial-corner spatial-corner-tr" />
          <div className="spatial-corner spatial-corner-bl" />
          <div className="spatial-corner spatial-corner-br" />
        </div>
      </section>

      <section className="warehouse-level-guide" aria-label="五级仓库导航">
        {warehouseLevelSteps.map((item, index) => {
          const state =
            index < currentStageIndex
              ? "is-done"
              : index === currentStageIndex
                ? "is-current"
                : "is-upcoming";
          const canNavigate = index < currentStageIndex;
          return (
            <Fragment key={item.level}>
              <button
                type="button"
                className={`warehouse-level-step ${state}`}
                disabled={!canNavigate}
                aria-current={state === "is-current" ? "step" : undefined}
                onClick={() => navigateStage(item.level)}
              >
                <span>{item.step}</span>
                <span className="warehouse-level-copy">
                  <strong>{item.title}</strong>
                  <small>{item.detail}</small>
                </span>
              </button>
              {index < warehouseLevelSteps.length - 1 && <MoveRight size={16} />}
            </Fragment>
          );
        })}
      </section>

      <section className="expiry-cockpit" aria-labelledby="expiry-cockpit-title">
        <header className="expiry-cockpit-head">
          <div>
            <span className="section-kicker">EXPIRY COMMAND CENTER · ¼ / ⅓ / ½</span>
            <h3 id="expiry-cockpit-title">先看效期，再决定放在哪。</h3>
            <p>库存按剩余有效期占总保质期的比例自动分类；同一屏汇总商品数量、库位与建议移入货架。</p>
          </div>
          <div className="expiry-live-stamp">
            <Clock3 size={15} />
            <span>上海实时日期</span>
            <strong>{expiryInsights.today}</strong>
            <small>每分钟刷新 · 随导入数据变化</small>
          </div>
        </header>

        <div className="expiry-summary-strip">
          <div>
            <span>临期商品</span>
            <strong>{formatMetric(expiryInsights.products.filter(
              (product) => ["quarter", "third", "half"].some(
                (category) => (product.quantityByCategory.get(category as ExpiryCategory) ?? 0) > 0,
              ),
            ).length)}</strong>
            <small>¼、⅓、½ 临期品项</small>
          </div>
          <div>
            <span>临期库存</span>
            <strong>{formatMetric(expiryTotals.nearQuantity)}</strong>
            <small>{formatMetric(expiryTotals.nearBatches)} 个批次</small>
          </div>
          <div>
            <span>涉及货架</span>
            <strong>{formatMetric(expiryTotals.occupiedLocations)}</strong>
            <small>已按真实库位聚合</small>
          </div>
          <div className={expiryInsights.warningCount ? "has-risk" : ""}>
            <span>需要处理</span>
            <strong>{formatMetric(expiryInsights.warningCount)}</strong>
            <small>{riskProductCount} 个高风险品项</small>
          </div>
        </div>

        <div className="expiry-toolbar">
          <div className="expiry-filter-tabs" role="group" aria-label="效期分类筛选">
            {([
              ["all", "全部"],
              ["quarter", "¼ 临期"],
              ["third", "⅓ 临期"],
              ["half", "½ 临期"],
              ["fresh", "新鲜期"],
              ["risk", "风险预警"],
            ] as const).map(([value, label]) => {
              const category = expiryInsights.categories.find((item) => item.category === value);
              const productCount = value === "all"
                ? expiryInsights.products.length
                : value === "risk"
                  ? riskProductCount
                  : expiryInsights.products.filter(
                    (product) => productMatchesExpiryView(product, value),
                  ).length;
              return (
                <button
                  type="button"
                  key={value}
                  className={expiryView === value ? "is-active" : ""}
                  aria-pressed={expiryView === value}
                  onClick={() => setExpiryView(value)}
                >
                  <span>{label}</span>
                  <b>{productCount}</b>
                  <small>
                    {value === "risk"
                      ? `${expiryInsights.warningCount} 条预警`
                      : `${formatMetric(category?.quantity ?? 0)} 件`}
                  </small>
                </button>
              );
            })}
          </div>
          <label className="expiry-search">
            <Search size={16} />
            <input
              value={expiryQuery}
              onChange={(event) => setExpiryQuery(event.target.value)}
              aria-label="搜索商品、SKU、批次或库位"
              placeholder="搜索商品、SKU、批次或库位"
            />
            {expiryQuery && (
              <button type="button" onClick={() => setExpiryQuery("")} aria-label="清空效期搜索">
                清除
              </button>
            )}
          </label>
        </div>

        <div className="expiry-cockpit-grid">
          <div className="expiry-product-catalog">
            <div className="expiry-catalog-heading">
              <div>
                <span>
                  {expiryView === "risk" ? "风险预警" : expiryCategoryLabel(expiryView)}
                </span>
                <strong>{visibleExpiryProducts.length} 个品项</strong>
              </div>
              <small>
                {expiryQuery
                  ? `搜索“${expiryQuery}”`
                  : "数量与位置同屏展示，点开任一品项查看批次"}
              </small>
            </div>

            {visibleExpiryProducts.length > 0 ? (
              <div className="expiry-product-list">
                {visibleExpiryProducts.map((product) => {
                  const expanded = expandedSku === product.sku;
                  const recommendedRacks = [...new Set(
                    product.batches
                      .map((batch) => batch.recommendedRack)
                      .filter((code): code is string => Boolean(code)),
                  )];
                  const batchCounts = expiryCategoryOrder
                    .map((category) => ({
                      category,
                      quantity: product.quantityByCategory.get(category) ?? 0,
                    }))
                    .filter((item) => item.quantity > 0);
                  return (
                    <article
                      className={`expiry-product-card ${expanded ? "is-expanded" : ""}`}
                      key={product.sku}
                    >
                      <button
                        type="button"
                        className="expiry-product-row"
                        aria-expanded={expanded}
                        onClick={() => setExpandedSku(expanded ? null : product.sku)}
                      >
                        <span className="expiry-product-identity">
                          <span className="expiry-product-name">
                            <strong>{product.product}</strong>
                            <i className={`expiry-reached-badge is-${product.mostUrgentClass}`}>
                              {expiryReachedLabel(product.mostUrgentClass)}
                            </i>
                          </span>
                          <small>{product.sku}</small>
                          <i className={`expiry-velocity ${
                            product.category === "爆旺"
                              ? "hot"
                              : product.category === "平滞"
                                ? "slow"
                                : "stable"
                          }`}>
                            {product.category}
                          </i>
                        </span>
                        <span className="expiry-product-mix" aria-label="各效期库存数量">
                          {batchCounts.map(({ category, quantity }) => (
                            <i className={`is-${category}`} key={category}>
                              <b>{expiryCategoryLabel(category)}</b>
                              <span>{formatMetric(quantity)}</span>
                            </i>
                          ))}
                        </span>
                        <span className="expiry-product-location">
                          <small>当前库位</small>
                          <strong>{product.locations.join(" · ") || "未提供"}</strong>
                          <em>最近节点 {formatExpiry(product.earliestExpiry)}</em>
                        </span>
                        <span className="expiry-product-plan">
                          <span>
                            <b className={`expiry-priority is-${product.representative.priorityLabel.toLowerCase()}`}>
                              {product.representative.priorityLabel}
                            </b>
                            <small>综合分 {product.representative.placementScore.toFixed(1)}</small>
                          </span>
                          <strong>
                            {recommendedRacks.length
                              ? `建议移入 ${recommendedRacks.slice(0, 2).join("、")}`
                              : "保持原位 / 隔离复核"}
                          </strong>
                          <em>
                            {product.daysToNextClass == null
                              ? "效期节点待核对"
                              : product.daysToNextClass === 0
                                ? `今日进入${expiryCategoryLabel(product.representative.nextClass ?? "expired")}`
                                : `${product.daysToNextClass} 天后进入${expiryCategoryLabel(product.representative.nextClass ?? "expired")}`}
                          </em>
                        </span>
                        <ChevronRight size={16} className="expiry-row-chevron" />
                      </button>

                      {expanded && (
                        <div className="expiry-batch-detail">
                          <div className="expiry-batch-detail-head">
                            <span>批次与摆放建议</span>
                            <small>{product.batches.length} 个批次 · 总量 {formatMetric(product.totalQuantity)} 件</small>
                          </div>
                          {product.batches.map((batch) => {
                            const pct = remainingRatioPercent(batch.snapshot.remainingRatio);
                            const transitionHint =
                              batch.daysToNextClass != null && batch.nextClass
                                ? batch.daysToNextClass === 0
                                  ? `今日进入 ${expiryCategoryLabel(batch.nextClass)}`
                                  : `${batch.daysToNextClass} 天后转入 ${expiryCategoryLabel(batch.nextClass)}`
                                : null;
                            return (
                              <div className="expiry-batch-line" key={batch.batch.id}>
                                <span>
                                  <strong>{batch.batch.lot}</strong>
                                  <small>{formatMetric(batch.batch.quantity)} 件</small>
                                </span>
                                <span className="expiry-batch-expiry">
                                  <i className={`expiry-class-tag is-${batch.snapshot.expiryClass}`}>
                                    {expiryCategoryLabel(batch.snapshot.expiryClass as ExpiryCategory)}
                                  </i>
                                  <small>到期 {formatExpiry(batch.snapshot.expiresOn)}</small>
                                  {pct != null && (
                                    <span className="expiry-batch-remaining">
                                      <i className="expiry-remaining-bar">
                                        <b style={{ width: `${pct}%` }} />
                                      </i>
                                      <em className={`expiry-remaining-pct is-${batch.snapshot.expiryClass}`}>
                                        剩 {pct}%
                                      </em>
                                    </span>
                                  )}
                                  {transitionHint && (
                                    <em className="expiry-transition-hint" title="期限变化预警">
                                      ⚠ {transitionHint}
                                    </em>
                                  )}
                                </span>
                                <button
                                  type="button"
                                  className="expiry-location-link"
                                  onClick={() => navigateToRack(normalizeLocationCode(batch.batch.location))}
                                >
                                  <MapPin size={12} /> {normalizeLocationCode(batch.batch.location) || "库位待补"}
                                  {batch.currentDistance != null && <small>{batch.currentDistance.toFixed(1)} m</small>}
                                </button>
                                <span className="expiry-batch-action">
                                  {batch.recommendedRack ? (
                                    <button
                                      type="button"
                                      onClick={() => navigateToRack(batch.recommendedRack!)}
                                    >
                                      <ArrowUpRight size={13} />
                                      <span>
                                        <small>{batch.recommendedBand}</small>
                                        <strong>{batch.recommendedRack}</strong>
                                      </span>
                                    </button>
                                  ) : (
                                    <em>{batch.batch.recallFlag ? "冻结 · 不分配" : "保持原位"}</em>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="expiry-empty-state">
                <Search size={24} />
                <strong>当前筛选没有匹配商品</strong>
                <p>试试商品名、SKU、批次号或完整库位编码，例如 {activeZone}01-01-03。</p>
                <button type="button" onClick={() => { setExpiryView("all"); setExpiryQuery(""); }}>
                  恢复全部效期
                </button>
              </div>
            )}
          </div>

          <aside className="expiry-risk-rail" aria-label="效期风险预警">
            <div className="expiry-risk-head">
              <span className="section-kicker">LIVE EXPIRY ALERTS</span>
              <div>
                <ShieldAlert size={18} />
                <strong>效期预警</strong>
                <b>{expiryInsights.warningCount}</b>
              </div>
              <p>阈值变化、过期、效期未知和召回批次集中显示；建议只代表待执行方案。</p>
            </div>
            <div className="expiry-risk-list">
              {(showAllWarnings ? expiryInsights.warnings : expiryInsights.warnings.slice(0, 6)).map((warning) => (
                <article className={`expiry-risk-card is-${warning.severity}`} key={warning.id}>
                  <div>
                    <i />
                    <span>{warning.title}</span>
                    <small>{warning.severity === "critical" ? "立即处理" : warning.severity === "warning" ? "临期预警" : "观察"}</small>
                  </div>
                  <strong>{warning.product}</strong>
                  <p>{warning.detail}</p>
                  <div className="expiry-risk-meta">
                    <span><MapPin size={11} />{warning.location}</span>
                    <span>{expiryCategoryLabel(warning.category)}</span>
                  </div>
                  {(warning.recommendedRack || normalizeLocationCode(warning.location)) && (
                    <button
                      type="button"
                      onClick={() => navigateToRack(
                        warning.recommendedRack ?? normalizeLocationCode(warning.location),
                      )}
                    >
                      {warning.recommendedRack ? `查看建议货架 ${warning.recommendedRack}` : "在地图定位当前货架"}
                      <ArrowRight size={13} />
                    </button>
                  )}
                </article>
              ))}
              {!expiryInsights.warnings.length && (
                <div className="expiry-risk-clear">
                  <PackageCheck size={22} />
                  <strong>当前没有效期风险</strong>
                  <p>分类与预警会随库存、批次和日期实时更新。</p>
                </div>
              )}
            </div>
            {expiryInsights.warnings.length > 6 && (
              <button
                type="button"
                className="expiry-more-warnings"
                onClick={() => setShowAllWarnings((current) => !current)}
              >
                {showAllWarnings ? "收起预警" : `展开全部 ${expiryInsights.warnings.length} 条预警`}
                <ChevronRight size={14} />
              </button>
            )}
          </aside>
        </div>
      </section>

      <section className="map-lower warehouse-inspector-grid">
        <div className="location-inspector">
          <div className="panel-head compact-head">
            <div>
              <span className="section-kicker">LIVE LOCATION INSPECTOR</span>
              <div className="location-title-row">
                <h3>{selected?.code ?? "选择货架"}</h3>
                {selected && (
                  <span className={`inventory-state-badge stock-${selected.stockState}`}>
                    <i /> {stockStateLabel(selected.stockState)}
                  </span>
                )}
              </div>
            </div>
            {selected && (
              <button className="icon-button focus-button" aria-label="定位当前货架" onClick={() => navigateToRack(selected.code)}>
                <Focus size={18} />
              </button>
            )}
          </div>

          {selected && (
            <>
              <div className="location-coordinates warehouse-coordinates">
                <div><span>原始编码</span><strong>{selected.rawCode}</strong></div>
                <div><span>规范编码</span><strong>{selected.code}</strong></div>
                <div><span>库区类型</span><strong>{activeZoneKind}</strong></div>
                <div><span>主通道</span><strong>{selected.mainAisle}</strong></div>
                <div><span>小通道</span><strong>{selected.smallAisle}</strong></div>
                <div><span>货架</span><strong>{selected.rack}</strong></div>
                <div><span>出货口路线</span><strong>{selected.distance.toFixed(1)} m</strong></div>
                <div><span>库位属性</span><strong>{selected.encoded ? "实体库位" : "虚拟库位"}</strong></div>
              </div>

              <div className="inventory-summary-grid">
                <div>
                  <PackageCheck size={15} />
                  <span>库存数量</span>
                  <strong>{selected.stockQuantity.toLocaleString("zh-CN")}</strong>
                </div>
                <div>
                  <Layers3 size={15} />
                  <span>SKU / 批次</span>
                  <strong>{selected.skuCount} / {selected.batchCount}</strong>
                </div>
                <div>
                  <Database size={15} />
                  <span>数据更新</span>
                  <strong>{formatInventoryUpdate(selectedInventory?.latestUpdate)}</strong>
                </div>
              </div>

              <div className="location-product inventory-product-panel">
                <span>{selectedProductNames.length ? `在库品项 · ${selectedProductNames.length}` : "库存数据"}</span>
                {selectedProducts.length ? (
                  <div className="inventory-product-list">
                    {selectedProducts.map((product) => (
                      <div key={product.sku}>
                        <strong>{product.product}</strong>
                        <small>
                          {product.sku} · {product.category} · 当前库存 {product.currentStock} · 预测日均 {product.forecastDaily.toFixed(1)}
                        </small>
                      </div>
                    ))}
                  </div>
                ) : selectedProductNames.length ? (
                  <div className="inventory-product-list">
                    {selectedProductNames.map((name) => (
                      <div key={name}>
                        <strong>{name}</strong>
                        <small>后端货名 · 等待 SKU、批次与效期明细同步</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <strong>{selected.stockState === "stocked" ? "已有库存记录" : "当前未关联品项"}</strong>
                    <small>
                      {selected.stockState === "stocked"
                        ? "货架状态来自库存与批次数据，导入后商品信息会在此同步。"
                        : "暂无有效库存或批次记录，状态随下一次导入实时更新。"}
                    </small>
                  </>
                )}
              </div>

              {selectedBatches.length > 0 && (
                <div className="inventory-batch-panel">
                  <div className="inventory-panel-heading">
                    <span>批次与效期</span>
                    <small>按到期日升序</small>
                  </div>
                  {selectedBatches.slice(0, 4).map((batch) => (
                    <div className="inventory-batch-row" key={batch.id}>
                      <div>
                        <strong>{batch.product}</strong>
                        <small>{batch.sku}</small>
                      </div>
                      <span>批次 {batch.lot}</span>
                      <span>{batch.quantity.toLocaleString("zh-CN")} 件</span>
                      <span>到期 {formatExpiry(batch.expiresOn)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={`route-explain ${selected.stockState === "unknown" ? "is-muted" : ""}`}>
                <Route size={17} />
                <div>
                  <span>真实路线距离模型</span>
                  <p>货架拣货边 → 小通道中心线 → 主通道交点 → 分发走廊 → 出货口。按 16 SVG 单位 / 米计算，货架按 2.0 × 0.6 米真实比例绘制。</p>
                </div>
              </div>

              {selectedPlacement ? (
                <div className="move-recommendation">
                  <div><LocateFixed size={17} /><span>建议拣货位</span></div>
                  <strong>{selectedPlacement.to}</strong>
                  <p>{selectedPlacement.reason}</p>
                  <button className="text-link" onClick={() => navigateToRack(selectedPlacement.to)}>
                    在地图中查看 <ArrowRight size={15} />
                  </button>
                </div>
              ) : (
                <div className="move-recommendation neutral">
                  <div><Layers3 size={17} /><span>当前策略</span></div>
                  <strong>保持原位</strong>
                  <p>当前货架暂无高优先级搬迁动作。库存和商品变化后，此判断会随数据更新。</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="map-statistics">
          <span className="section-kicker">
            {isSpecialZone ? "SPECIAL LOCATIONS · NAMED INDEX" : `${activeZone} ZONE · LIVE CAPACITY`}
          </span>
          <h3>
            {isSpecialZone
              ? <>特殊库位，<br />名称总汇全量。</>
              : <>{activeZone} {activeZoneKind}，<br />标准 Q 型布局。</>}
          </h3>
          {isSpecialZone ? (
            <>
              <div className="space-stat">
                <span>特殊库位</span><strong>{layout.nodes.length}</strong><small>名称总汇中的非标准名称</small>
              </div>
              <div className="space-stat">
                <span>已关联库存</span><strong>{layout.stats.stocked}</strong><small>可直接点击查看货物与批次</small>
              </div>
              <div className="space-stat">
                <span>待核对</span><strong>{layout.stats.unknown}</strong><small>等待库存数据关联</small>
              </div>
            </>
          ) : (
            <>
              <div className="space-stat">
                <span>主通道</span><strong>{layout.stats.mainAisles}</strong><small>浅蓝色贯通通道</small>
              </div>
              <div className="space-stat">
                <span>小通道</span><strong>{layout.stats.smallAisles}</strong><small>按真实库位动态生成</small>
              </div>
              <div className="space-stat">
                <span>货架总数</span><strong>{layout.stats.racks}</strong><small>{layout.stats.stocked} 有货 · {layout.stats.empty} 空架 · {layout.stats.unknown} 待核对</small>
              </div>
              <button className="secondary-button full" onClick={openFullMap}>
                打开完整库位图 <Maximize2 size={15} />
              </button>
            </>
          )}
        </div>

        <div className="layout-guide warehouse-map-spec">
          <span className="section-kicker">LIVE MAP SPEC</span>
          <div className="guide-icon"><Boxes size={24} /></div>
          <h3>{isSpecialZone ? "名称总汇，直达库存" : "真实尺度，实时状态"}</h3>
          <p>
            {isSpecialZone
              ? "特殊库位不套用标准通道编码；点击任一名称可直接打开对应库存详情。"
              : "货架长 2.0 米、宽 0.6 米，长边沿小通道方向。所有主通道的 01 号小通道均靠近出货口。"}
          </p>
          <div className="warehouse-spec-list">
            {isSpecialZone ? (
              <>
                <span><b>{layout.nodes.length}</b> 个名称总汇特殊库位</span>
                <span><b>{layout.stats.stocked}</b> 个已关联库存</span>
                <span><b>{layout.stats.unknown}</b> 个待核对</span>
              </>
            ) : (
              <>
                <span><b>{layout.stats.mainAisles}</b> {activeZone} 区真实主通道</span>
                <span><b>{layout.stats.smallAisles}</b> 条真实小通道</span>
                <span><b>{layout.stats.racks}</b> 个真实货架库位</span>
              </>
            )}
          </div>
          <div className="spec-help">
            <CircleHelp size={14} />
            <span>滚轮缩放、拖拽平移、点击下钻；Esc 返回上一层。</span>
          </div>
        </div>
      </section>

      <section className="move-ledger">
        <div className="directory-toolbar">
          <div>
            <span className="section-kicker">PLACEMENT LEDGER</span>
            <h3>搬迁台账直接定位到货架</h3>
          </div>
          <span className="ledger-count">共 {plan.placements.length} 条 · 点击行进入对应货架</span>
        </div>
        <div className="move-table">
          <div className="move-row move-row-head">
            <span>品项</span><span>热度</span><span>原库位</span><span>建议库位</span><span>路线改善</span><span>判断</span>
          </div>
          {plan.placements.slice(0, 12).map((item) => {
            const improvement = item.fromDistance - item.toDistance;
            return (
              <button className="move-row" key={item.sku} onClick={() => navigateToRack(item.to)}>
                <span className="move-product"><strong>{item.product}</strong><small>{item.sku}</small></span>
                <span><i className={`category-dot ${item.category === "爆旺" ? "hot" : item.category === "平滞" ? "slow" : "stable"}`} />{item.category}</span>
                <span>{item.from}</span>
                <span className="move-target">{item.to}</span>
                <span className={improvement >= 0 ? "positive" : "negative"}>{improvement >= 0 ? "缩短" : "增加"} {Math.abs(improvement).toFixed(1)} m</span>
                <span className="reason-cell">{item.reason}</span>
              </button>
            );
          })}
        </div>
      </section>

      <span className="sr-only" aria-live="polite">
        {isSpecialZone
          ? `名称总汇共 ${WAREHOUSE_SPECIAL_LOCATIONS.length} 个特殊库位；当前查看特殊库位总览，${layout.stats.stocked} 个已关联库存，${layout.stats.unknown} 个待核对；当前为${levelLabel(selection.level, true)}`
          : `全仓共 ${WAREHOUSE_ZONE_COUNT} 个真实库区、${WAREHOUSE_LOCATION_COUNT.toLocaleString("zh-CN")} 个库位；当前查看 ${activeZone} ${activeZoneKind}，共 ${layout.stats.racks} 个货架，${layout.stats.stocked} 个有货，${layout.stats.empty} 个空架，${layout.stats.unknown} 个待核对；当前为${levelLabel(selection.level)}`}
        {selection.mainAisle ? `，主通道 ${selection.mainAisle}` : ""}
        {selection.smallAisle ? `，小通道 ${selection.smallAisle}` : ""}。
        {selection.level === "rack" && selection.selectedCode ? ` 当前货架 ${selection.selectedCode}。` : ""}
      </span>
    </div>
  );
}
